import type { ReactNode } from 'react';
import { cx } from '../cx';
import { TICK_TEXT, niceScale, percentOf } from './scale';
import { NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';

export type ScatterPoint = { id: string; label: string; x: number; y: number };

export type ScatterPlotProps = {
  points: ScatterPoint[];
  /** Where the quadrant lines sit (usually the medians), and which quadrant the headline is about. */
  split: { x: number; y: number; quadrant?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' };
  /** The quadrant's name, written in its corner ("In demand and well paid"). */
  quadrantLabel: ReactNode;
  /** Axis captions ("More open jobs", "Higher pay"). */
  xLabel: ReactNode;
  yLabel: ReactNode;
  formatX: (v: number) => string;
  formatY: (v: number) => string;
  /** Points to name besides those in the quadrant (named points are always labelled). */
  labelIds?: string[];
  /** `auto` (default) switches to the narrow form in a narrow container: a shorter plot with at most three points named on it (`labelIds` first, then the quadrant's points), the other names and the quadrant label in a key under it. */
  layout?: ChartLayout;
  /** One sentence that states the finding, for screen readers. */
  description: string;
  className?: string;
};

/**
 * One dot per item placed by two measures, with dashed lines at the split and the quadrant that
 * matters named. Points in that quadrant are sun and labelled; the rest context.
 */
const PLOT_H: Record<ChartLayout, string> = { auto: 'h-48 @md:h-64', narrow: 'h-48', wide: 'h-64' };
/** In the narrow form at most this many points keep their label on the plot. */
const NARROW_LABELS = 3;

export function ScatterPlot({
  points,
  split,
  quadrantLabel,
  xLabel,
  yLabel,
  formatX,
  formatY,
  labelIds = [],
  layout = 'auto',
  description,
  className,
}: ScatterPlotProps) {
  const xs = niceScale(Math.min(...points.map((p) => p.x), split.x), Math.max(...points.map((p) => p.x), split.x), {
    zero: true,
  });
  const ys = niceScale(Math.min(...points.map((p) => p.y), split.y), Math.max(...points.map((p) => p.y), split.y));
  const q = split.quadrant ?? 'top-right';
  const inQuadrant = (p: ScatterPoint) =>
    (q.endsWith('right') ? p.x >= split.x : p.x < split.x) && (q.startsWith('top') ? p.y >= split.y : p.y < split.y);
  const px = (v: number) => percentOf(v, xs.min, xs.max);
  const py = (v: number) => 100 - percentOf(v, ys.min, ys.max);
  // Every labelled point, in the order labelIds gives, then the rest of the quadrant; the first few stay on the narrow plot.
  const labelled = [
    ...labelIds.map((id) => points.find((p) => p.id === id)).filter((p): p is ScatterPoint => !!p),
    ...points.filter((p) => inQuadrant(p) && !labelIds.includes(p.id)),
  ];
  const onNarrow = new Set(labelled.slice(0, NARROW_LABELS).map((p) => p.id));
  const keyed = labelled.filter((p) => !onNarrow.has(p.id));
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <span className={cx('pl-14', TICK_TEXT)} aria-hidden="true">
        {yLabel}
      </span>
      <div className="flex gap-2" aria-hidden="true">
        <div className={cx('relative w-12 shrink-0', PLOT_H[layout])}>
          {ys.ticks.map((t) => (
            <span key={t} className={cx('absolute right-0 -translate-y-1/2', TICK_TEXT)} style={{ top: `${py(t)}%` }}>
              {formatY(t)}
            </span>
          ))}
        </div>
        <div className={cx('relative mr-2 flex-1 border-b border-l border-jc-chart-grid', PLOT_H[layout])}>
          <span
            className="absolute inset-y-0 border-l-2 border-dashed border-jc-ink-secondary"
            style={{ left: `${px(split.x)}%` }}
          />
          <span
            className="absolute inset-x-0 border-t-2 border-dashed border-jc-ink-secondary"
            style={{ top: `${py(split.y)}%` }}
          />
          <span
            className={cx(
              'absolute px-1 text-xs font-semibold text-jc-ink-secondary',
              WIDE_ONLY[layout],
              q.startsWith('top') ? 'top-1' : 'bottom-1',
              q.endsWith('right') ? 'right-1 text-right' : 'left-1',
            )}
          >
            {quadrantLabel}
          </span>
          {points.map((p) => {
            const hl = inQuadrant(p);
            const named = hl || labelIds.includes(p.id);
            const nearRight = px(p.x) > 75;
            return (
              <span key={p.id} className="absolute" style={{ left: `${px(p.x)}%`, top: `${py(p.y)}%` }}>
                <span
                  className={cx(
                    'absolute -translate-1/2 rounded-full',
                    hl ? 'z-10 size-3 bg-jc-chart-2 ring-2 ring-jc-ink' : 'size-2.5 bg-jc-chart-context',
                  )}
                />
                {named ? (
                  <span
                    className={cx(
                      'absolute -translate-y-1/2 text-xs whitespace-nowrap',
                      nearRight ? 'right-2' : 'left-2',
                      hl ? 'font-semibold text-jc-ink' : 'text-jc-ink-secondary',
                      !onNarrow.has(p.id) && WIDE_ONLY[layout],
                    )}
                  >
                    {p.label}
                  </span>
                ) : null}
              </span>
            );
          })}
        </div>
      </div>
      <div className="relative mr-2 ml-14 h-4" aria-hidden="true">
        {xs.ticks.map((t) => (
          <span key={t} className={cx('absolute -translate-x-1/2', TICK_TEXT)} style={{ left: `${px(t)}%` }}>
            {formatX(t)}
          </span>
        ))}
      </div>
      <span className={cx('self-end', TICK_TEXT)} aria-hidden="true">
        {xLabel}
      </span>
      <div
        className={cx('flex flex-col gap-1 pl-14 type-meta text-jc-ink-secondary', NARROW_ONLY[layout])}
        aria-hidden="true"
      >
        <span className="font-semibold text-jc-ink-secondary">{quadrantLabel}</span>
        {keyed.length ? (
          <span className="flex flex-wrap gap-x-4 gap-y-0.5">
            {keyed.map((p) => (
              <span key={p.id}>{p.label}</span>
            ))}
          </span>
        ) : null}
      </div>
    </div>
  );
}
